import { useState, useEffect, useCallback } from 'react';
import { Plus, MagnifyingGlass, Cube, X, Pencil, Trash, Check, Warning, Package } from '@phosphor-icons/react';
import api from '../api';
import WorkflowActions from '../components/WorkflowActions';
import { useAuth } from '../contexts/AuthContext';

const INK_COLORS = { Blue: '#1a6fb5', Red: '#c0392b', Black: '#1a1a1a', Green: '#27ae60',
  '4-Color': '#8e44ad', Assorted: '#7f8c8d' };

const BLANK_FORM = {
  sku: '', name: '', product_family: 'Whiteboard Marker', status: 'active',
  unit_cost: '', selling_price: '',
  customer_id: '', customer_name: '',
  batch_tracked: false,
  attributes: { ink_color: 'Blue', tip_type: 'Bullet', packing_qty: '10', carton_qty: '120' },
  bom: [],
};

const LBL = { display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
  color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 };
const INP = { width: '100%', background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,16%)',
  padding: '8px 10px', color: 'hsl(0,0%,85%)', fontFamily: "'JetBrains Mono', monospace",
  fontSize: 11, outline: 'none', boxSizing: 'border-box' };
const INP_SM = { ...INP, padding: '5px 8px', fontSize: 10 };

function StatusChip({ status }) {
  const map = { active: 'chip-active', development: 'chip-warning', obsolete: 'chip-neutral' };
  return <span className={map[status] || 'chip-neutral'}>{status?.toUpperCase()}</span>;
}

function ColorDot({ color }) {
  return (
    <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
      background: INK_COLORS[color] || '#666', border: '1px solid hsl(0,0%,30%)' }} />
  );
}

function BOMEditor({ bom, onChange, uoms = [] }) {
  const add = () => onChange([...bom, { material: '', qty: '', unit: uoms[0]?.code || 'g' }]);
  const remove = (i) => onChange(bom.filter((_, idx) => idx !== i));
  const set = (i, key, val) => {
    const next = bom.map((row, idx) => idx === i ? { ...row, [key]: val } : row);
    onChange(next);
  };
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <label style={LBL}>Bill of Materials</label>
        <button type="button" onClick={add} data-testid="add-bom-row"
          style={{ background: 'none', border: '1px solid hsl(197,100%,48%)', color: 'hsl(197,100%,48%)',
            padding: '3px 10px', fontSize: 10, cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace" }}>
          + Add Row
        </button>
      </div>
      {bom.length === 0 && (
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,35%)',
          padding: '8px 10px', border: '1px dashed hsl(0,0%,18%)', textAlign: 'center' }}>
          No materials — click "+ Add Row"
        </div>
      )}
      {bom.map((row, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 80px 28px', gap: 6, marginBottom: 5 }}>
          <input value={row.material} placeholder="Material name"
            onChange={e => set(i, 'material', e.target.value)} style={INP_SM} />
          <input value={row.qty} placeholder="Qty" type="number" step="any"
            onChange={e => set(i, 'qty', e.target.value)} style={INP_SM} />
          {uoms.length > 0 ? (
            <select value={row.unit} onChange={e => set(i, 'unit', e.target.value)}
              style={{ ...INP_SM, appearance: 'auto' }}>
              {uoms.map(u => <option key={u.id || u.code} value={u.code}>{u.code}</option>)}
            </select>
          ) : (
            <input value={row.unit} placeholder="Unit" maxLength={10}
              onChange={e => set(i, 'unit', e.target.value)} style={INP_SM} />
          )}
          <button type="button" onClick={() => remove(i)}
            style={{ background: 'none', border: '1px solid hsl(0,84%,40%)', color: 'hsl(0,84%,60%)',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={11} />
          </button>
        </div>
      ))}
    </div>
  );
}

export default function Products() {
  const [products, setProducts]   = useState([]);
  const [customers, setCustomers] = useState([]);
  const [uoms, setUoms]           = useState([]);
  const [prodCats, setProdCats]   = useState([]);
  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState(null);
  const [editMode, setEditMode]   = useState(false);
  const [showAdd, setShowAdd]     = useState(false);
  const [filter, setFilter]       = useState('all');
  const [variantFilter, setVariantFilter] = useState('all'); // all | standard | custom
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState(BLANK_FORM);
  const [editForm, setEditForm]   = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const { user } = useAuth();

  const load = useCallback(() => {
    Promise.all([
      api.get('/products'), api.get('/customers'),
      api.get('/masters/uoms'), api.get('/masters/product-categories'),
    ])
      .then(([pr, cr, ur, pcr]) => {
        setProducts(pr.data); setCustomers(cr.data);
        setUoms(ur.data); setProdCats(pcr.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // When a product is selected, open it in detail view
  const openProduct = (p) => {
    setSelected(p);
    setEditMode(false);
    setDeleteConfirm(false);
    setEditForm({
      ...p,
      unit_cost: p.unit_cost?.toString() || '',
      selling_price: p.selling_price?.toString() || '',
      attributes: { ...{ ink_color:'Blue', tip_type:'Bullet', packing_qty:'10', carton_qty:'120' }, ...(p.attributes||{}),
        packing_qty: String(p.attributes?.packing_qty || 10),
        carton_qty: String(p.attributes?.carton_qty || 120),
      },
      bom: p.bom || [],
    });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/products', {
        ...form,
        unit_cost: parseFloat(form.unit_cost) || 0,
        selling_price: parseFloat(form.selling_price) || 0,
        attributes: { ...form.attributes,
          packing_qty: parseInt(form.attributes.packing_qty) || 10,
          carton_qty: parseInt(form.attributes.carton_qty) || 120 },
        bom: form.bom.filter(r => r.material).map(r => ({ ...r, qty: parseFloat(r.qty)||1 })),
        customer_id: form.customer_id || null,
        customer_name: form.customer_name || null,
      });
      load();
      setShowAdd(false);
      setForm(BLANK_FORM);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSaving(true);
    try {
      const updated = await api.put(`/products/${selected.id}`, {
        ...editForm,
        unit_cost: parseFloat(editForm.unit_cost) || 0,
        selling_price: parseFloat(editForm.selling_price) || 0,
        attributes: { ...editForm.attributes,
          packing_qty: parseInt(editForm.attributes?.packing_qty) || 10,
          carton_qty: parseInt(editForm.attributes?.carton_qty) || 120 },
        bom: (editForm.bom||[]).filter(r => r.material).map(r => ({ ...r, qty: parseFloat(r.qty)||1 })),
        customer_id: editForm.customer_id || null,
        customer_name: editForm.customer_name || null,
      });
      load();
      setSelected(updated.data);
      setEditMode(false);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await api.delete(`/products/${selected.id}`);
      load(); setSelected(null); setDeleteConfirm(false);
    } catch (e) { console.error(e); }
  };

  const families = ['all', ...new Set(products.map(p => p.product_family))];

  const filtered = products.filter(p => {
    const matchSearch = !search || p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.sku?.toLowerCase().includes(search.toLowerCase());
    const matchFamily = filter === 'all' || p.product_family === filter;
    const matchVariant = variantFilter === 'all' ||
      (variantFilter === 'custom' && p.customer_id) ||
      (variantFilter === 'standard' && !p.customer_id);
    return matchSearch && matchFamily && matchVariant;
  });

  const margin = (p) => p.selling_price && p.unit_cost ?
    (((p.selling_price - p.unit_cost) / p.selling_price) * 100).toFixed(0) : '—';

  // ── Customer picker helper ─────────────────────────────────────────────────
  const CustomerSelect = ({ value, onChange, testId }) => (
    <select value={value} onChange={e => {
      const cust = customers.find(c => c.id === e.target.value);
      onChange(e.target.value, cust?.name || '');
    }} data-testid={testId} style={{ ...INP }}>
      <option value="">— Standard (no customer) —</option>
      {customers.map(c => <option key={c.id} value={c.id}>{c.name} ({c.code || c.id.slice(-4)})</option>)}
    </select>
  );

  // ── Attribute editor row ─────────────────────────────────────────────────
  const AttrFields = ({ attrs, setAttrs }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      <div>
        <label style={LBL}>Ink Color</label>
        <select value={attrs.ink_color} onChange={e => setAttrs(p => ({ ...p, ink_color: e.target.value }))} style={{ ...INP }}>
          {Object.keys(INK_COLORS).map(c => <option key={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <label style={LBL}>Tip Type</label>
        <select value={attrs.tip_type} onChange={e => setAttrs(p => ({ ...p, tip_type: e.target.value }))} style={{ ...INP }}>
          {['Bullet','Chisel','Fine','Ultra-fine','Broad'].map(t => <option key={t}>{t}</option>)}
        </select>
      </div>
      <div>
        <label style={LBL}>Packing Qty</label>
        <input type="number" value={attrs.packing_qty} onChange={e => setAttrs(p => ({ ...p, packing_qty: e.target.value }))} style={{ ...INP }} />
      </div>
      <div>
        <label style={LBL}>Carton Qty</label>
        <input type="number" value={attrs.carton_qty} onChange={e => setAttrs(p => ({ ...p, carton_qty: e.target.value }))} style={{ ...INP }} />
      </div>
    </div>
  );

  return (
    <div data-testid="products-page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 22, fontWeight: 700,
            color: 'hsl(0,0%,98%)', textTransform: 'uppercase', marginBottom: 4 }}>Products & BOM</h1>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,45%)',
            textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            {products.length} SKUs · Item Master · Customer Variants
          </div>
        </div>
        <button data-testid="add-product-btn" onClick={() => { setForm(BLANK_FORM); setShowAdd(true); }}
          className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} weight="bold" /> NEW SKU
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative' }}>
          <MagnifyingGlass size={13} color="hsl(0,0%,40%)"
            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search SKU or name..."
            data-testid="product-search"
            style={{ background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,16%)', padding: '7px 10px 7px 30px',
              color: 'hsl(0,0%,80%)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              outline: 'none', width: 200 }} />
        </div>
        <div style={{ width: 1, height: 24, background: 'hsl(0,0%,15%)' }} />
        {families.map(f => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-family-${f}`}
            style={{ padding: '5px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer', border: '1px solid',
              borderColor: filter === f ? 'hsl(197,100%,48%)' : 'hsl(0,0%,18%)',
              background: filter === f ? 'hsl(197,100%,48%)' : 'transparent',
              color: filter === f ? '#000' : 'hsl(0,0%,55%)', transition: 'all 0.15s' }}>
            {f === 'all' ? 'All Families' : f}
          </button>
        ))}
        <div style={{ width: 1, height: 24, background: 'hsl(0,0%,15%)' }} />
        {[{ k:'all', label:'All Variants' }, { k:'standard', label:'Standard' }, { k:'custom', label:'Customer-Specific' }].map(v => (
          <button key={v.k} onClick={() => setVariantFilter(v.k)} data-testid={`filter-variant-${v.k}`}
            style={{ padding: '5px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer', border: '1px solid',
              borderColor: variantFilter === v.k ? 'hsl(12,90%,55%)' : 'hsl(0,0%,18%)',
              background: variantFilter === v.k ? 'hsl(12,90%,55%)' : 'transparent',
              color: variantFilter === v.k ? '#000' : 'hsl(0,0%,55%)', transition: 'all 0.15s' }}>
            {v.label}
          </button>
        ))}
      </div>

      {/* Split view */}
      <div className="split-view" style={{ gap: 1, height: 'calc(100vh - 290px)' }}>
        {/* Left: product list */}
        <div className="split-left" style={{ width: 360, background: 'hsl(0,0%,4%)', border: '1px solid hsl(0,0%,14%)', overflowY: 'auto' }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid hsl(0,0%,12%)',
            fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,45%)',
            textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            {filtered.length} of {products.length} SKUs
          </div>
          {loading ? (
            <div style={{ padding: 20, color: 'hsl(0,0%,40%)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>Loading...</div>
          ) : filtered.map(p => (
            <div key={p.id} data-testid={`product-card-${p.sku}`}
              onClick={() => openProduct(p)}
              style={{ padding: '11px 14px', borderBottom: '1px solid hsl(0,0%,10%)', cursor: 'pointer',
                background: selected?.id === p.id ? 'hsl(0,0%,7%)' : 'transparent',
                borderLeft: p.customer_id ? '3px solid hsl(12,90%,55%)' :
                  p.status === 'development' ? '3px solid hsl(43,96%,56%)' :
                  p.status === 'obsolete' ? '3px solid hsl(0,0%,25%)' : '3px solid transparent',
                transition: 'background 0.1s' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    {p.attributes?.ink_color && <ColorDot color={p.attributes.ink_color} />}
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                      color: 'hsl(197,100%,48%)', fontWeight: 700 }}>{p.sku}</span>
                  </div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                    color: 'hsl(0,0%,85%)', marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {p.name}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {p.attributes?.tip_type && (
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                        color: 'hsl(0,0%,50%)', background: 'hsl(0,0%,8%)', padding: '1px 5px',
                        border: '1px solid hsl(0,0%,15%)' }}>
                        {p.attributes.tip_type}
                      </span>
                    )}
                    {p.customer_id && (
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                        color: 'hsl(12,90%,55%)', background: 'hsl(12,90%,10%)', padding: '1px 5px',
                        border: '1px solid hsl(12,90%,25%)' }}>
                        {p.customer_name || 'CUSTOM'}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                    color: 'hsl(197,100%,48%)', fontWeight: 700, marginBottom: 2 }}>
                    ₹{p.selling_price?.toFixed(0)}
                  </div>
                  <StatusChip status={p.status} />
                  <WorkflowActions recordType="product" record={p} userRole={user?.role} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Right: detail / edit */}
        <div className="split-right" style={{ flex: 1, background: 'hsl(0,0%,4%)', border: '1px solid hsl(0,0%,14%)', overflowY: 'auto' }}>
          {!selected ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', height: '100%', color: 'hsl(0,0%,30%)', gap: 12 }}>
              <Package size={40} />
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
                textTransform: 'uppercase', letterSpacing: '0.15em' }}>Select a product</div>
            </div>
          ) : !editMode ? (
            // ── Read mode ────────────────────────────────────────────────────────
            <div data-testid="product-detail" style={{ padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid hsl(0,0%,12%)' }}>
                <div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                    color: 'hsl(197,100%,48%)', fontWeight: 700, marginBottom: 4 }}>{selected.sku}</div>
                  <h2 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 18, fontWeight: 700,
                    color: 'hsl(0,0%,95%)', textTransform: 'uppercase' }}>{selected.name}</h2>
                  {selected.customer_id && (
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                      color: 'hsl(12,90%,55%)', marginTop: 4 }}>
                      Customer Variant: {selected.customer_name}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <StatusChip status={selected.status} />
                  <button onClick={() => setEditMode(true)} data-testid="edit-product-btn"
                    className="btn-ghost" style={{ padding: '5px 10px', fontSize: 11,
                      display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Pencil size={12} />Edit
                  </button>
                  <button onClick={() => setDeleteConfirm(true)} data-testid="delete-product-btn"
                    className="btn-ghost" style={{ padding: '5px 10px', fontSize: 11,
                      color: 'hsl(0,84%,60%)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Trash size={12} />
                  </button>
                </div>
              </div>

              {deleteConfirm && (
                <div style={{ background: 'hsl(0,30%,8%)', border: '1px solid hsl(0,60%,30%)',
                  padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Warning size={16} color="hsl(0,84%,60%)" />
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,80%)', flex: 1 }}>
                    Delete {selected.sku}? This cannot be undone.
                  </span>
                  <button onClick={handleDelete} data-testid="confirm-delete-btn"
                    style={{ background: 'hsl(0,70%,40%)', color: '#fff', border: 'none',
                      padding: '5px 12px', cursor: 'pointer', fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
                    Delete
                  </button>
                  <button onClick={() => setDeleteConfirm(false)}
                    style={{ background: 'none', border: '1px solid hsl(0,0%,25%)', color: 'hsl(0,0%,55%)',
                      padding: '5px 12px', cursor: 'pointer', fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
                    Cancel
                  </button>
                </div>
              )}

              {/* Pricing grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, marginBottom: 16 }}>
                {[
                  { label: 'Unit Cost', val: `₹${selected.unit_cost?.toFixed(2)}`, color: 'hsl(0,0%,70%)' },
                  { label: 'Selling Price', val: `₹${selected.selling_price?.toFixed(2)}`, color: 'hsl(197,100%,48%)' },
                  { label: 'Gross Margin', val: `${margin(selected)}%`, color: 'hsl(142,60%,45%)' },
                  { label: 'Product Family', val: selected.product_family, color: 'hsl(0,0%,70%)' },
                ].map(k => (
                  <div key={k.label} style={{ background: 'hsl(0,0%,6%)', border: '1px solid hsl(0,0%,12%)', padding: 12 }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,40%)',
                      textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>{k.label}</div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: k.color, fontWeight: 700 }}>{k.val}</div>
                  </div>
                ))}
              </div>

              {/* Attributes */}
              <div style={{ background: 'hsl(0,0%,6%)', border: '1px solid hsl(0,0%,12%)', padding: 14, marginBottom: 12 }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,40%)',
                  textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 10 }}>Attributes</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
                  {Object.entries(selected.attributes || {}).map(([k, v]) => (
                    <div key={k}>
                      <div style={{ fontSize: 9, color: 'hsl(0,0%,40%)', fontFamily: "'JetBrains Mono', monospace",
                        textTransform: 'uppercase', marginBottom: 2 }}>{k.replace(/_/g,' ')}</div>
                      <div style={{ fontSize: 11, color: 'hsl(0,0%,75%)', fontFamily: "'JetBrains Mono', monospace",
                        display: 'flex', alignItems: 'center', gap: 4 }}>
                        {k === 'ink_color' && <ColorDot color={String(v)} />}
                        {String(v)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* BOM */}
              <div style={{ background: 'hsl(0,0%,6%)', border: '1px solid hsl(0,0%,12%)', padding: 14 }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,40%)',
                  textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 10 }}>
                  Bill of Materials ({selected.bom?.length || 0} items)
                </div>
                {(!selected.bom || selected.bom.length === 0) ? (
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,35%)' }}>
                    No BOM defined — click Edit to add materials
                  </div>
                ) : selected.bom.map((b, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between',
                    padding: '6px 0', borderBottom: '1px solid hsl(0,0%,9%)' }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,70%)' }}>
                      {b.material}
                    </span>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(197,100%,48%)', fontWeight: 700 }}>
                      {b.qty} {b.unit}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // ── Edit mode ────────────────────────────────────────────────────────
            <form data-testid="edit-product-form" onSubmit={handleSaveEdit} style={{ padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: 20, paddingBottom: 14, borderBottom: '1px solid hsl(0,0%,12%)' }}>
                <h3 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16, textTransform: 'uppercase',
                  color: 'hsl(0,0%,95%)', margin: 0 }}>Edit — {selected.sku}</h3>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => setEditMode(false)} className="btn-ghost" style={{ fontSize: 11 }}>Cancel</button>
                  <button type="submit" data-testid="save-edit-btn" disabled={saving} className="btn-primary" style={{ fontSize: 11 }}>
                    <Check size={12} style={{ marginRight: 4 }} />{saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={LBL}>Product Name *</label>
                  <input required value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} style={INP} />
                </div>
                <div>
                  <label style={LBL}>Product Family</label>
                  {prodCats.length > 0 ? (
                    <select value={editForm.product_family}
                      onChange={e => setEditForm(p => ({ ...p, product_family: e.target.value }))}
                      style={{ ...INP }}>
                      {prodCats.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                      {/* preserve current value if not in list */}
                      {editForm.product_family && !prodCats.find(c => c.name === editForm.product_family) && (
                        <option value={editForm.product_family}>{editForm.product_family}</option>
                      )}
                    </select>
                  ) : (
                    <input value={editForm.product_family} onChange={e => setEditForm(p => ({ ...p, product_family: e.target.value }))} style={INP} />
                  )}
                </div>
                <div>
                  <label style={LBL}>Unit Cost (₹)</label>
                  <input type="number" step="any" value={editForm.unit_cost} onChange={e => setEditForm(p => ({ ...p, unit_cost: e.target.value }))} style={INP} />
                </div>
                <div>
                  <label style={LBL}>Selling Price (₹)</label>
                  <input type="number" step="any" value={editForm.selling_price} onChange={e => setEditForm(p => ({ ...p, selling_price: e.target.value }))} style={INP} />
                </div>
                <div>
                  <label style={LBL}>Status</label>
                  <select value={editForm.status} onChange={e => setEditForm(p => ({ ...p, status: e.target.value }))} style={{ ...INP }}>
                    {['active','development','obsolete'].map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label style={LBL}>Customer-Specific Variant</label>
                  <CustomerSelect value={editForm.customer_id || ''} testId="edit-customer-select"
                    onChange={(id, name) => setEditForm(p => ({ ...p, customer_id: id, customer_name: name }))} />
                </div>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      data-testid="product-batch-tracked-checkbox"
                      checked={editForm.batch_tracked || false}
                      onChange={e => setEditForm({...editForm, batch_tracked: e.target.checked})}
                      style={{ width: 'auto', margin: 0 }}
                    />
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,85%)' }}>Batch Tracked</span>
                  </label>
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ ...LBL, marginBottom: 8 }}>Attributes</label>
                <AttrFields attrs={editForm.attributes || {}}
                  setAttrs={fn => setEditForm(p => ({ ...p, attributes: typeof fn === 'function' ? fn(p.attributes) : fn }))} />
              </div>

              <BOMEditor bom={editForm.bom || []}
                onChange={bom => setEditForm(p => ({ ...p, bom }))}
                uoms={uoms} />
            </form>
          )}
        </div>
      </div>

      {/* Add SKU Modal */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,18%)',
            width: 600, maxHeight: '90vh', overflowY: 'auto', padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16,
                textTransform: 'uppercase', color: 'hsl(0,0%,95%)' }}>New SKU</h3>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none',
                cursor: 'pointer', color: 'hsl(0,0%,50%)' }}><X size={18} /></button>
            </div>
            <form onSubmit={handleAdd}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={LBL}>SKU Code *</label>
                  <input required value={form.sku} data-testid="field-sku"
                    onChange={e => setForm(p => ({ ...p, sku: e.target.value }))} style={INP} />
                </div>
                <div>
                  <label style={LBL}>Product Family *</label>
                  {prodCats.length > 0 ? (
                    <select value={form.product_family} data-testid="field-product_family"
                      onChange={e => setForm(p => ({ ...p, product_family: e.target.value }))}
                      style={{ ...INP }}>
                      {prodCats.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                  ) : (
                    <input required value={form.product_family} data-testid="field-product_family"
                      onChange={e => setForm(p => ({ ...p, product_family: e.target.value }))} style={INP} />
                  )}
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={LBL}>Product Name *</label>
                  <input required value={form.name} data-testid="field-name"
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))} style={INP} />
                </div>
                <div>
                  <label style={LBL}>Unit Cost (₹) *</label>
                  <input required type="number" step="any" value={form.unit_cost} data-testid="field-unit_cost"
                    onChange={e => setForm(p => ({ ...p, unit_cost: e.target.value }))} style={INP} />
                </div>
                <div>
                  <label style={LBL}>Selling Price (₹) *</label>
                  <input required type="number" step="any" value={form.selling_price} data-testid="field-selling_price"
                    onChange={e => setForm(p => ({ ...p, selling_price: e.target.value }))} style={INP} />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={LBL}>Customer-Specific Variant (optional)</label>
                  <CustomerSelect value={form.customer_id} testId="add-customer-select"
                    onChange={(id, name) => setForm(p => ({ ...p, customer_id: id, customer_name: name }))} />
                  {form.customer_name && (
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                      color: 'hsl(12,90%,55%)', marginTop: 4 }}>
                      Variant locked to: {form.customer_name}
                    </div>
                  )}
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      data-testid="product-batch-tracked-checkbox"
                      checked={form.batch_tracked || false}
                      onChange={e => setForm({...form, batch_tracked: e.target.checked})}
                      style={{ width: 'auto', margin: 0 }}
                    />
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,85%)' }}>Batch Tracked</span>
                  </label>
                </div>
              </div>

              <div style={{ marginBottom: 14, padding: '12px 14px', background: 'hsl(0,0%,7%)',
                border: '1px solid hsl(0,0%,14%)' }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,40%)',
                  textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 10 }}>Product Attributes</div>
                <AttrFields attrs={form.attributes}
                  setAttrs={fn => setForm(p => ({ ...p, attributes: typeof fn === 'function' ? fn(p.attributes) : fn }))} />
              </div>

              <div style={{ padding: '12px 14px', background: 'hsl(0,0%,7%)',
                border: '1px solid hsl(0,0%,14%)', marginBottom: 20 }}>
                <BOMEditor bom={form.bom} onChange={bom => setForm(p => ({ ...p, bom }))} uoms={uoms} />
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowAdd(false)} className="btn-ghost">Cancel</button>
                <button type="submit" data-testid="save-product-btn" disabled={saving} className="btn-primary">
                  {saving ? 'Saving...' : 'SAVE SKU'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
